import { useEffect, useState, useRef, useCallback } from 'react';
import { Image } from 'react-bootstrap';

import { nightMode } from '../../assets/images/images';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { hide, show, themeBlackSelector } from '../../store/features/theme';

const ThemeChange = () => {
    const dispatch = useAppDispatch();
    const themeBlack = useAppSelector(themeBlackSelector);
    const [isDarkTheme, setIsDarkTheme] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const labelRef = useRef<HTMLLabelElement>(null);

    const resetFadeTimer = useCallback(() => {
        // Clear existing timer
        if (fadeTimeoutRef.current) {
            clearTimeout(fadeTimeoutRef.current);
        }
        
        // Remove fade-out class immediately
        setFadeOut(false);
        
        // Set fade-out class after 3 seconds
        fadeTimeoutRef.current = setTimeout(() => {
            setFadeOut(true);
        }, 3000);
    }, []);

    const handleThemeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
          dispatch(show());
        } else {
          dispatch(hide());
        }
        // Reset fade timer on toggle
        resetFadeTimer();
    };
    
    const handleInteraction = useCallback(() => {
        resetFadeTimer();
    }, [resetFadeTimer]);

    useEffect(() => {
        setIsDarkTheme(themeBlack);
        if (themeBlack) {
            document.body.classList.add('theme-dark');
        } else {
            document.body.classList.remove('theme-dark');
        }
    }, [themeBlack]);
    
    useEffect(() => {
        // Start timer on mount
        resetFadeTimer();
        
        return () => {
            // Cleanup timer on unmount
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
            }
        };
    }, [resetFadeTimer]);

    // Sun icon SVG (shown when dark mode is active)
    const SunIcon = () => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="toggle-image">
            <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" fill="currentColor"/>
            <path d="M12 2V4M12 20V22M4 12H2M22 12H20M19.07 4.93L17.66 6.34M6.34 17.66L4.93 19.07M19.07 19.07L17.66 17.66M6.34 6.34L4.93 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
    );

    return (
        <>
            <div className="toggle-theme-btn">
                <input type="checkbox" id="toggle" className="toggle-input" checked={isDarkTheme}
                        onChange={handleThemeToggle} />
                <label 
                    ref={labelRef}
                    htmlFor="toggle" 
                    className={`toggle-label ${fadeOut ? 'fade-out' : ''}`}
                    onMouseEnter={handleInteraction}
                    onTouchStart={handleInteraction}
                >
                    <span className="toggle-slider"></span>
                    {isDarkTheme ? (
                        <SunIcon />
                    ) : (
                        <Image src={nightMode} alt="night mode" className="toggle-image" />
                    )}
                </label>
            </div>
        </>
    );
};

export default ThemeChange;
